import { useEffect, useRef } from 'react';
import { callCenterApi } from './api';
import { useCallCenter } from './store';

/**
 * Keep the agent's basket in step with what the server would actually charge.
 *
 * Promotions and membership benefits are priced entirely on the server — the
 * rules, the scoping and the stacking live there, and a membership benefit also
 * depends on how much of a monthly allowance the customer has already used
 * elsewhere today. So the only way an agent can tell a caller what they owe is
 * to ask.
 *
 * This is NOT cosmetic. The server applies these discounts whether or not we
 * asked, and then refuses a payment that does not match them — which is exactly
 * how "Send order" came to fail silently on a member's basket. Getting the
 * quote wrong costs the tenant a sale; not showing it costs the agent their
 * credibility on the phone.
 *
 * Every input the server scopes by has to travel with the basket and has to be
 * the SAME value the order will be sent with — branch, customer, order type,
 * delivery fee. A quote asked with a different branch than the send is worse
 * than no quote: it shows a discount the order then refuses.
 *
 * Debounced, because an agent adding five items would otherwise fire five
 * requests for a basket that settles once. Cheap either way — the endpoint is
 * read-only and reserves nothing.
 */
export function useEngineQuote(): void {
  const lines = useCallCenter((s) => s.lines);
  const branchId = useCallCenter((s) => s.branchId);
  const customerId = useCallCenter((s) => s.customer?.id ?? null);
  const deliveryFee = useCallCenter((s) => s.deliveryFee);
  const orderType = useCallCenter((s) => s.bootstrap?.settings.defaultOrderType ?? 2);
  const couponCode = useCallCenter((s) => s.couponCode);
  const discount = useCallCenter((s) => s.discount);
  const setEngineQuote = useCallCenter((s) => s.setEngineQuote);
  const setQuotePending = useCallCenter((s) => s.setQuotePending);

  // Guards against a slow response for an old basket landing after a fast one
  // for the current basket and overwriting it.
  const seq = useRef(0);

  useEffect(() => {
    if (branchId == null || lines.length === 0) {
      setEngineQuote({ discount: 0, breakdown: [], couponDiscount: 0, couponError: null });
      setQuotePending(false);
      return;
    }

    const mine = ++seq.current;
    setQuotePending(true);

    const timer = setTimeout(() => {
      void callCenterApi.agent
        .quote({
          branch_id: branchId,
          items: useCallCenter.getState().toPayload(),
          discount_value: discount,
          delivery_fee: deliveryFee,
          customer_id: customerId,
          order_type: orderType,
          coupon_code: couponCode.trim() || null,
        })
        .then((r) => {
          if (mine !== seq.current) return;
          setEngineQuote({
            discount: r.engineDiscount ?? 0,
            breakdown: r.engineBreakdown ?? [],
            couponDiscount: r.couponDiscount ?? 0,
            couponError: r.couponError ?? null,
          });
        })
        .catch(() => {
          // A dropped quote must not leave a STALE discount on screen — the
          // agent would read out a number the order then refuses. Falling back
          // to zero over-quotes rather than under-quotes, and the send itself
          // re-prices server-side either way.
          if (mine !== seq.current) return;
          setEngineQuote({ discount: 0, breakdown: [], couponDiscount: 0, couponError: null });
        })
        .finally(() => {
          if (mine === seq.current) setQuotePending(false);
        });
    }, 300);

    return () => clearTimeout(timer);
    // `lines` is the identity of the array, which changes on every basket edit
    // — exactly when a re-quote is wanted.
  }, [
    lines, branchId, customerId, deliveryFee, orderType, couponCode, discount,
    setEngineQuote, setQuotePending,
  ]);
}
